import {action, computed, observable} from 'mobx';
import audioManager from '../../utils/AudioManager';
import {guid} from '../../utils/commonUtility';
import {RecorderState} from '../../constants/enums';
import mean from 'lodash/mean';
import {allowPlayback, AudioRecorder} from "../../utils/audioUtility";
import looperStore from '../../containers/create/looper/store'
import appStore from '../../containers/app/store';
import {isChrome, isDesktop, isEdgeChromium} from "../../utils/screenUtility";
import {getUserProperties, setUserProperties} from "../../utils/userUtility";

class RecorderStore {
    @observable isPlaying = false;
    @observable player = null;
    @observable recorderInstance = null;
    @observable inputDeviceId = null;
    @observable monitorIsActive = false;
    @observable recorderState = RecorderState.Init;
    @observable recorderMaxScale = 0;
    @observable samples = [];
    @observable isValid = false;
    @observable playbackMode = allowPlayback();
    @observable allowPlayback = this.playbackMode;
    @observable warningModal = false;

    recording = null;
    changedDefaultPlaybackState = false;


    getRecordCurrentTime = () => {
        return audioManager.getCurrentTime(this.player);
    }

    getRecordDuration = () => {
        return audioManager.getDuration(this.player);
    }

    setRecordCurrentTime = (time) => {
        return audioManager.setCurrentTime(this.player, time);
    }

    @action setAllowPlayback = (val) => {
        if (!this.changedDefaultPlaybackState && !this.playbackMode) {
            this.allowPlayback = val;
            this.setPlaybackMode(this.allowPlayback);
            this.changedDefaultPlaybackState = false;
        }
    };

    @action setPlaybackMode = (val) => {
        this.changedDefaultPlaybackState = true;
        this.playbackMode = val;
        if (val && !looperStore.isLoopersEmpty) {
            appStore.createAlertFromMessageId('success', 'upload.recorder.playbackToast', '', 3000);
        }
    };

    @action refreshRecorder = () => {
        if (this.recorderInstance) {
            this.setRecorderInstance(new AudioRecorder(this.recorderInstance.bpm, this.recorderInstance.onaudioprocessCb, this.recorderInstance.onDoneRecoding));
            this.recorderInstance.init();
        }
    }

    @action setRecorderInstance = (newInstance) => {
        this.recorderInstance = newInstance;
        if (newInstance) {
            if (allowPlayback() && !looperStore.isLoopersEmpty) {
                appStore.createAlertFromMessageId('success', 'upload.recorder.playbackToast', '', 3000);
            }
            if (isDesktop() && (!isChrome() && !isEdgeChromium())) {
                const showRecorderModal = getUserProperties().showRecorderModal || false;
                if (!showRecorderModal) {
                    this.showWarningModal();
                    setUserProperties({showRecorderModal: true})
                } else {
                    appStore.createAlertFromMessageId('success', 'recorder.browser.warning', '', 3000);
                }
            }

        }
        // this.setPlaybackMode(this.playbackMode && allowPlayback());
    };

    @action setChannelId = (channelId) => {
        this.channelId = channelId;
    };

    @action setRecorderState = (newState) => {
        // console.log('setting state: ' + newState + ', monitor state: ' + this.monitorIsActive);
        this.recorderState = newState;
        switch (this.recorderState) {
            case RecorderState.Init:
            case RecorderState.Countdown:
                if (this.recorderInstance) {
                    this.recorderInstance.setMonitorState(this.monitorIsActive);
                }
                break;
            case RecorderState.Stop:
                if (this.recorderInstance) {
                    this.recorderInstance.setMonitorState(false);
                }
                break;
            default:
                break;
        }
    };

    @action
    setIsPlaying(newState) {
        this.isPlaying = newState;
    };

    @action
    setInputDeviceId(newState) {
        this.inputDeviceId = newState;
    };

    @action toggleMonitorState = (val) => {
        this.monitorIsActive = val;
    };

    @action storeRecording = async () => {
        this.recording = this.recorderInstance.output;
        this.isValid = this.recording !== null;
        this.player = await audioManager.subscribe({fileId: guid(), buffer: this.recording, useMonoVersion: false, speed: 1}, {
            onStopPause: () => {
                this.setIsPlaying(false);
            }
        });
    };

    playRecordInstance = async () => {
        audioManager.play([this.player]);
        this.setIsPlaying(true);
    };

    stopRecordInstance = () => {
        audioManager.stop([this.player]);
        this.setIsPlaying(false);
    };

    softReset = () => {
        this.stopRecordInstance();
        this.recording = null;
    }

    destroyInstance = () => {
        if (this.recorderInstance) this.recorderInstance.destroy();
        this.recording = null;
    }

    @action setRecorderMaxScale = (val) => {
        this.recorderMaxScale = val;
        this.samples.push(val);
        if (this.samples.length > 100) {
            this.samples.splice(0, 1);
        }
    }

    @action showWarningModal = () => {
        this.warningModal = true;
    }

    @action hideWarningModal = () => {
        this.warningModal = false;
    }

    @computed
    get tooQuite() {
        return mean(this.samples) < 0.03;
    }
}

const recorderStore = new RecorderStore();
export default recorderStore;
